/**
 * Admin → Emails tab.
 *
 * Email templates list (grouped by category) with per-row enable/preview/
 * edit/test actions. The edit form and the email logs view are inline
 * expanding panels. Preview shows the rendered HTML in an iframe so the
 * email's own styles don't leak into the dashboard.
 *
 * Ported from `routes/admin.ts`.
 */
import { useEffect, useRef, useState } from 'hono/jsx';
import { AdminShell } from '../../components/AdminShell';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { Spinner } from '../../components/Spinner';

interface EmailTemplate {
  id: string;
  name: string;
  description?: string | null;
  category: string;
  isEnabled: boolean;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  availableTokens?: string[];
}

interface EmailLog {
  createdAt: string;
  templateName?: string | null;
  recipientEmail: string;
  subject: string;
  status: 'sent' | 'failed' | 'skipped' | 'bounced' | string;
  errorMessage?: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Authentication',
  subscription: 'Subscription',
  payment: 'Payment',
  affiliate: 'Affiliate',
  admin: 'Admin Notifications',
};

type Panel = 'none' | 'edit' | 'preview' | 'logs';

export function AdminEmails() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('');

  const [panel, setPanel] = useState<Panel>('none');
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [preview, setPreview] = useState<{ subject: string; bodyHtml: string; bodyText: string } | null>(null);
  const [logs, setLogs] = useState<{ logs: EmailLog[]; total: number } | null>(null);

  async function load(cat = category) {
    setLoading(true);
    setLoadError(null);
    try {
      const url = '/admin/email-templates' + (cat ? '?category=' + encodeURIComponent(cat) : '');
      const r = await api(url);
      if (!r.ok) throw new Error('Failed to load email templates');
      const data = await r.json() as { templates?: EmailTemplate[] };
      setTemplates(data.templates || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(''); }, []);

  function onCategoryChange(e: Event) {
    const v = (e.target as HTMLSelectElement).value;
    setCategory(v);
    load(v);
  }

  function closePanels() {
    setPanel('none');
    setEditing(null);
    setPreview(null);
    setLogs(null);
  }

  async function toggle(id: string) {
    try {
      const r = await api('/admin/email-templates/' + id + '/toggle', { method: 'POST' });
      if (!r.ok) throw new Error('Failed to toggle template');
      load();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  async function previewTemplate(id: string) {
    try {
      const r = await api('/admin/email-templates/' + id + '/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error('Failed to preview template');
      const data = await r.json() as { subject: string; bodyHtml: string; bodyText: string };
      setPreview(data);
      setEditing(null);
      setLogs(null);
      setPanel('preview');
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  function editTemplate(id: string) {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setEditing(t);
    setPreview(null);
    setLogs(null);
    setPanel('edit');
  }

  async function testTemplate(id: string) {
    const email = prompt('Enter email address to send test:');
    if (!email) return;
    try {
      const r = await api('/admin/email-templates/' + id + '/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: email }),
      });
      const result = await r.json() as { error?: string; message?: string };
      if (!r.ok) throw new Error(result.error || 'Failed to send test email');
      showToast('success', 'Test Email Sent', result.message || '');
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  async function showLogs() {
    try {
      const r = await api('/admin/email-logs?limit=50');
      if (!r.ok) throw new Error('Failed to load email logs');
      const data = await r.json() as { logs: EmailLog[]; pagination: { total: number } };
      setLogs({ logs: data.logs || [], total: data.pagination?.total || 0 });
      setEditing(null);
      setPreview(null);
      setPanel('logs');
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    }
  }

  // Group by category for rendering.
  const grouped: Record<string, EmailTemplate[]> = {};
  for (const t of templates) {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  }

  return (
    <AdminShell active="emails">
      <div class="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-slate-100">Email Templates</h2>
        <div class="flex gap-2">
          <select
            value={category}
            onChange={onCategoryChange}
            class="px-3 py-1.5 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          >
            <option value="">All Categories</option>
            <option value="auth">Authentication</option>
            <option value="subscription">Subscription</option>
            <option value="payment">Payment</option>
            <option value="affiliate">Affiliate</option>
            <option value="admin">Admin</option>
          </select>
          <button type="button" onClick={showLogs} class="px-3 py-1.5 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 hover:bg-dark-600 transition-colors">View Logs</button>
        </div>
      </div>

      {panel === 'edit' && editing && (
        <EditPanel
          template={editing}
          onClose={closePanels}
          onSaved={() => { closePanels(); load(); }}
        />
      )}

      {panel === 'preview' && preview && (
        <PreviewPanel preview={preview} onClose={closePanels} />
      )}

      {panel === 'logs' && logs && (
        <LogsPanel data={logs} onClose={closePanels} />
      )}

      <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-hidden">
        {loading ? (
          <div class="p-8 text-center"><Spinner className="mx-auto" /><p class="text-slate-400 mt-4 text-sm">Loading email templates...</p></div>
        ) : loadError ? (
          <div class="p-8 text-center text-red-400">{loadError}</div>
        ) : !templates.length ? (
          <div class="p-8 text-center text-slate-400">No email templates found</div>
        ) : (
          Object.keys(grouped).map((cat) => (
            <div class="border-b border-slate-700/50 last:border-b-0">
              <div class="px-4 py-3 bg-dark-700">
                <h3 class="text-sm font-medium text-slate-300">{CATEGORY_LABELS[cat] || cat}</h3>
              </div>
              <div class="divide-y divide-slate-700/30">
                {grouped[cat].map((t) => (
                  <div class="px-4 py-3 flex items-center justify-between hover:bg-dark-700/30">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <button type="button" onClick={() => toggle(t.id)} class="flex-shrink-0" aria-label="Toggle">
                        <span class={`w-9 h-5 rounded-full transition-colors flex items-center ${t.isEnabled ? 'bg-cyan-500' : 'bg-slate-600'}`}>
                          <span class={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${t.isEnabled ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`}></span>
                        </span>
                      </button>
                      <div class="min-w-0">
                        <div class="text-sm font-medium text-slate-200 truncate">{t.name}</div>
                        <div class="text-xs text-slate-400 truncate">{t.description || ''}</div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2 flex-shrink-0 ml-4">
                      <button type="button" onClick={() => previewTemplate(t.id)} class="text-slate-400 hover:text-slate-200 p-1.5" title="Preview">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      </button>
                      <button type="button" onClick={() => editTemplate(t.id)} class="text-slate-400 hover:text-slate-200 p-1.5" title="Edit">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>
                      <button type="button" onClick={() => testTemplate(t.id)} class="text-slate-400 hover:text-slate-200 p-1.5" title="Send Test">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}

function EditPanel({ template, onClose, onSaved }: { template: EmailTemplate; onClose: () => void; onSaved: () => void }) {
  const [subject, setSubject] = useState<string>(template.subject);
  const [bodyHtml, setBodyHtml] = useState<string>(template.bodyHtml);
  const [bodyText, setBodyText] = useState<string>(template.bodyText);
  const [description, setDescription] = useState<string>(template.description || '');
  const [busy, setBusy] = useState<boolean>(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api('/admin/email-templates/' + template.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, bodyHtml, bodyText, description }),
      });
      if (!r.ok) throw new Error('Failed to update template');
      showToast('success', 'Template Updated', 'Email template has been saved');
      onSaved();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-cyan-500/30 shadow-xl">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Edit Email Template</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <form onSubmit={onSubmit} class="p-6 space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Template Name</label>
            <input type="text" value={template.name} disabled class="w-full px-3 py-2 bg-dark-900 border border-slate-700/50 rounded-lg text-slate-400 cursor-not-allowed" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Category</label>
            <input type="text" value={template.category} disabled class="w-full px-3 py-2 bg-dark-900 border border-slate-700/50 rounded-lg text-slate-400 cursor-not-allowed" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Description</label>
          <input type="text" value={description} onInput={(e) => setDescription((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Subject</label>
          <input type="text" value={subject} onInput={(e) => setSubject((e.target as HTMLInputElement).value)} required class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">HTML Body</label>
          <textarea
            rows={10}
            required
            value={bodyHtml}
            onInput={(e) => setBodyHtml((e.target as HTMLTextAreaElement).value)}
            class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 font-mono text-sm"
          ></textarea>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Plain Text Body</label>
          <textarea
            rows={6}
            required
            value={bodyText}
            onInput={(e) => setBodyText((e.target as HTMLTextAreaElement).value)}
            class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 font-mono text-sm"
          ></textarea>
        </div>
        {template.availableTokens && template.availableTokens.length > 0 && (
          <div class="bg-dark-700/50 rounded-lg p-3">
            <label class="block text-xs font-medium text-slate-400 uppercase mb-2">Available Tokens</label>
            <div class="flex flex-wrap gap-2">
              {template.availableTokens.map((token) => (
                <code class="text-xs bg-dark-900 px-2 py-1 rounded text-cyan-400">{`{{${token}}}`}</code>
              ))}
            </div>
          </div>
        )}
        <div class="flex justify-end gap-3 pt-4">
          <button type="button" onClick={onClose} class="px-4 py-2 text-slate-300 hover:text-slate-100">Cancel</button>
          <button type="submit" disabled={busy} class="px-4 py-2 bg-cyan-500 text-dark-900 rounded-lg font-medium hover:bg-cyan-400 transition-colors disabled:opacity-60">
            {busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PreviewPanel({ preview, onClose }: { preview: { subject: string; bodyHtml: string; bodyText: string }; onClose: () => void }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(preview.bodyHtml);
    doc.close();
    // Resize iframe to its content height.
    try {
      iframe.style.height = (doc.body.scrollHeight + 20) + 'px';
    } catch {
      // ignore if body not yet available
    }
  }, [preview.bodyHtml]);

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-slate-700/50 shadow-xl">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Email Preview</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="p-6">
        <div class="mb-4">
          <label class="text-xs text-slate-400 uppercase">Subject</label>
          <p class="text-slate-200 font-medium">{preview.subject}</p>
        </div>
        <div class="mb-4">
          <label class="text-xs text-slate-400 uppercase mb-2 block">HTML Preview</label>
          <div class="bg-white rounded-lg overflow-hidden" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <iframe ref={iframeRef} class="w-full" style={{ minHeight: '300px', border: 'none' }}></iframe>
          </div>
        </div>
        <div>
          <label class="text-xs text-slate-400 uppercase mb-2 block">Plain Text</label>
          <pre class="text-sm text-slate-300 bg-dark-700 p-4 rounded-lg overflow-auto max-h-40 whitespace-pre-wrap">{preview.bodyText}</pre>
        </div>
      </div>
    </div>
  );
}

function LogsPanel({ data, onClose }: { data: { logs: EmailLog[]; total: number }; onClose: () => void }) {
  const statusColors: Record<string, string> = {
    sent: 'bg-emerald-500/20 text-emerald-400',
    failed: 'bg-red-500/20 text-red-400',
    skipped: 'bg-amber-500/20 text-amber-400',
    bounced: 'bg-red-500/20 text-red-400',
  };

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-slate-700/50 shadow-xl overflow-x-auto">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Email Logs</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-dark-700">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Date</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Template</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Recipient</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Subject</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Error</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-700/50">
            {data.logs.length === 0 ? (
              <tr><td colspan={6} class="px-4 py-8 text-center text-slate-400">No email logs yet</td></tr>
            ) : data.logs.map((log) => {
              const errTrim = log.errorMessage ? (log.errorMessage.substring(0, 50) + (log.errorMessage.length > 50 ? '...' : '')) : '-';
              return (
                <tr class="hover:bg-dark-700/50">
                  <td class="px-4 py-3 text-sm text-slate-400">{new Date(log.createdAt).toLocaleString()}</td>
                  <td class="px-4 py-3 text-sm text-slate-200">{log.templateName || '-'}</td>
                  <td class="px-4 py-3 text-sm text-slate-300">{log.recipientEmail}</td>
                  <td class="px-4 py-3 text-sm text-slate-300 max-w-xs truncate" title={log.subject}>{log.subject}</td>
                  <td class="px-4 py-3">
                    <span class={`text-xs px-2 py-1 rounded ${statusColors[log.status] || 'bg-slate-500/20 text-slate-400'}`}>{log.status}</span>
                  </td>
                  <td class="px-4 py-3 text-xs text-red-400 max-w-xs truncate" title={log.errorMessage || ''}>{errTrim}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div class="px-6 py-4 border-t border-slate-700/50 text-sm text-slate-400">
        Showing {data.logs.length} of {data.total} logs
      </div>
    </div>
  );
}

export default AdminEmails;
